import { useCallback, useState } from "react";
import { List, ListItemButton, ListItemIcon, Checkbox, ListItemText, Button, Paper } from "@mui/material";

import Grid from '@mui/material/Grid';

/**
 * TransferList
 */

type Props<T extends {}> = {
  sourceList: T[],
  destinationList: T[],
  sourceListTitle: string,
  destinationListTitle: string,
  prefix?: string,
  getItemKey: (value: T) => number | string,
  getItemLabel: (value: T) => number | string,
  onSelectionChange: (leftList: T[], rightList: T[]) => void,
};

export const TransferList = <T extends {},>({
  sourceList,
  destinationList,
  sourceListTitle,
  destinationListTitle,
  prefix,
  getItemKey,
  getItemLabel,
  onSelectionChange,
}: Props<T>) => {
  const [checkedLeftList, setCheckedLeftList] = useState<T[]>([]);
  const [checkedRightList, setCheckedRightList] = useState<T[]>([]);

  const handleSelectAll = useCallback(() => {
    onSelectionChange([], [...sourceList, ...destinationList]);
    setCheckedLeftList([]);
    setCheckedRightList([]);
  }, [
    sourceList,
    destinationList,
    onSelectionChange,
  ]);

  const handleSelect = useCallback(() => {
    onSelectionChange(
      [...sourceList.filter(x => !checkedLeftList.some(y => getItemKey(x) === getItemKey(y)))],
      [...destinationList, ...checkedLeftList],
    );
    setCheckedLeftList([]);
  }, [
    sourceList,
    destinationList,
    checkedLeftList,
    getItemKey,
    onSelectionChange,
  ]);

  const handleDeselect = useCallback(() => {
    onSelectionChange(
      [...sourceList, ...checkedRightList],
      [...destinationList.filter(x => !checkedRightList.some(y => getItemKey(x) === getItemKey(y)))],
    );
    setCheckedRightList([]);
  }, [
    sourceList,
    destinationList,
    checkedRightList,
    getItemKey,
    onSelectionChange,
  ]);

  const handleDeselectAll = useCallback(() => {
    onSelectionChange([...sourceList, ...destinationList], []);
    setCheckedLeftList([]);
    setCheckedRightList([]);
  }, [
    sourceList,
    destinationList,
    onSelectionChange,
  ]);

  const handleLeftCheckedItemChange = useCallback((item: T) => {
    if (!checkedLeftList.some(x => getItemKey(x) === getItemKey(item))) setCheckedLeftList(lst => [...lst, item]);
    else setCheckedLeftList(lst => [...lst.filter(x => getItemKey(x) !== getItemKey(item))]);
  }, [
    checkedLeftList,
    getItemKey,
  ]);

  const handleRightCheckedItemChange = useCallback((item: T) => {
    if (!checkedRightList.some(x => getItemKey(x) === getItemKey(item))) setCheckedRightList(lst => [...lst, item]);
    else setCheckedRightList(lst => [...lst.filter(x => getItemKey(x) !== getItemKey(item))]);
  }, [
    checkedRightList,
    getItemKey,
  ]);

  return (
    <Grid
      container={true}
      spacing={2}
      sx={{ justifyContent: 'center', alignItems: 'center' }}>
      <Grid size={{ xs: 12, md: 5, lg: 5, xl: 4 }}>
        <Grid
          container={true}
          direction="column"
          sx={{ alignItems: 'center' }}>
          <span className="w-full bg-[#f3565d] px-2 rounded-t text-white text-center">
            {sourceListTitle}
          </span>
          <Paper
            square={true}
            sx={{ width: '100%', height: '30rem', overflow: 'auto' }}>
            <List
              dense={true}
              component="div"
              role="list">
              {sourceList.map(item => (
                <ListItemButton
                  key={getItemKey(item)}
                  role="listitem"
                  onClick={() => handleLeftCheckedItemChange(item)}
                  sx={{ paddingVertical: 0 }}>
                  <ListItemIcon>
                    <Checkbox
                      size="small"
                      checked={checkedLeftList.some(x => getItemKey(x) === getItemKey(item))}
                      tabIndex={-1}
                      disableRipple={true}
                      slotProps={{
                        input: {
                          'aria-labelledby': 'src-' + (prefix ?? 'item-') + getItemKey(item),
                        }
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    id={'src-' + (prefix ?? 'item-') + getItemKey(item)}
                    primary={getItemLabel(item)} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Grid size={{ xs: 12, md: 2, lg: 1, xl: 1 }}>
        <Grid
          container={true}
          direction="column"
          sx={{ alignItems: 'center' }}>
          <Button
            sx={{ my: 0.5, color: '#009C4E', borderColor: '#009C4E' }}
            variant="outlined"
            size="small"
            onClick={handleSelectAll}
            disabled={sourceList.length === 0}
            aria-label="Selecionar tudo">
            ≫
          </Button>
          <Button
            sx={{ my: 0.5, color: '#009C4E', borderColor: '#009C4E' }}
            variant="outlined"
            size="small"
            onClick={handleSelect}
            disabled={checkedLeftList.length === 0}
            aria-label="Selecionar">
            &gt;
          </Button>
          <span className="w-full h-[1rem]"></span>
          <Button
            sx={{ my: 0.5, color: '#009C4E', borderColor: '#009C4E' }}
            variant="outlined"
            size="small"
            onClick={handleDeselect}
            disabled={checkedRightList.length === 0}
            aria-label="Remover da seleção">
            &lt;
          </Button>
          <Button
            sx={{ my: 0.5, color: '#009C4E', borderColor: '#009C4E' }}
            variant="outlined"
            size="small"
            onClick={handleDeselectAll}
            disabled={destinationList.length === 0}
            aria-label="Remover toda a seleção">
            ≪
          </Button>
        </Grid>
      </Grid>

      <Grid size={{ xs: 12, md: 5, lg: 5, xl: 4 }}>
        <Grid
          container={true}
          direction="column"
          sx={{ alignItems: 'center' }}>
          <span className="w-full bg-[#45b6af] px-2 rounded text-white text-center">
            {destinationListTitle}
          </span>
          <Paper
            square={true}
            sx={{ width: '100%', height: '30rem', overflow: 'auto' }}>
            <List
              dense={true}
              component="div"
              role="list">
              {destinationList.map(item => (
                <ListItemButton
                  key={getItemKey(item)}
                  role="listitem"
                  onClick={() => handleRightCheckedItemChange(item)}
                  sx={{ paddingVertical: 0 }}>
                  <ListItemIcon>
                    <Checkbox
                      size="small"
                      checked={checkedRightList.some(x => getItemKey(x) === getItemKey(item))}
                      tabIndex={-1}
                      disableRipple={true}
                      slotProps={{
                        input: {
                          'aria-labelledby': 'dst-' + (prefix ?? 'item-') + getItemKey(item),
                        }
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    id={'dst-' + (prefix ?? 'item-') + getItemKey(item)}
                    primary={getItemLabel(item)} />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Grid>

  );
}